/**
 * Storage service that provides localStorage with cookie fallback for iPhone Safari compatibility
 * Handles cases where localStorage is unavailable or throws QuotaExceededError
 */

interface StorageInterface {
  setItem(key: string, value: string): boolean;
  getItem(key: string): string | null;
  removeItem(key: string): void;
  clear(): void;
}

class StorageService implements StorageInterface {
  private localStorageAvailable: boolean | null = null;

  /**
   * Test if localStorage is actually available and working
   * iPhone Safari private mode makes localStorage appear available but throws on write
   */
  private isLocalStorageAvailable(): boolean {
    if (this.localStorageAvailable !== null) {
      return this.localStorageAvailable;
    }

    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      this.localStorageAvailable = true;
      return true;
    } catch (e) {
      console.warn('localStorage not available, falling back to cookies:', e instanceof Error ? e.message : String(e));
      this.localStorageAvailable = false;
      return false;
    }
  }

  /**
   * Set cookie with secure defaults
   */
  private setCookie(name: string, value: string, days: number = 7): void {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      
      // Use secure flag in production, SameSite for CSRF protection
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      const sameSite = '; SameSite=Lax';
      
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/${secure}${sameSite}`;
    } catch (e) {
      console.error('Failed to set cookie:', e);
    }
  }

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    try {
      const nameEQ = name + '=';
      const cookies = document.cookie.split(';');
      
      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.indexOf(nameEQ) === 0) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }
      return null;
    } catch (e) {
      console.error('Failed to get cookie:', e);
      return null;
    }
  }

  /**
   * Remove cookie by setting expiry date in the past
   */
  private removeCookie(name: string): void {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${secure}`;
    } catch (e) {
      console.error('Failed to remove cookie:', e);
    }
  }

  /**
   * Store data with automatic fallback to cookies
   */
  setItem(key: string, value: string): boolean {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.setItem(key, value);
        return true;
      } else {
        this.setCookie(key, value);
        return true;
      }
    } catch (e) {
      console.error(`Failed to store ${key}:`, e);
      /*
       * One failed write used to switch this device to cookies for good:
       * every later write — the token, the user, the pets — went to a
       * cookie (dropped silently above ~4KB) and every read looked there
       * first, so a client who had signed in was "signed out" on one page
       * and signed in on the next, and the pets she could see on one
       * screen were "none" on another. A write can fail for one value
       * (QuotaExceededError on something big) while localStorage is
       * perfectly healthy. Retry the one value in a cookie if it can fit
       * there, and leave localStorage on for everything else. Only the
       * probe in isLocalStorageAvailable decides the fallback.
       */
      if (this.localStorageAvailable === true && value.length < 3500) {
        console.warn(`localStorage failed for ${key}, storing this value in a cookie`);
        try {
          this.setCookie(key, value);
          return true;
        } catch (cookieError) {
          console.error(`Cookie fallback also failed for ${key}:`, cookieError);
        }
      }
      return false;
    }
  }

  /**
   * Retrieve data with automatic fallback to cookies
   */
  getItem(key: string): string | null {
    try {
      if (this.isLocalStorageAvailable()) {
        const value = localStorage.getItem(key);
        // If localStorage returns null, check cookies as fallback
        if (value === null) {
          return this.getCookie(key);
        }
        return value;
      } else {
        return this.getCookie(key);
      }
    } catch (e) {
      console.error(`Failed to retrieve ${key}:`, e);
      // If localStorage failed, try cookie as fallback
      return this.getCookie(key);
    }
  }

  /**
   * Remove data from both localStorage and cookies
   */
  removeItem(key: string): void {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.removeItem(key);
      }
      this.removeCookie(key);
    } catch (e) {
      console.error(`Failed to remove ${key}:`, e);
      // Still try to remove cookie even if localStorage failed
      this.removeCookie(key);
    }
  }

  /**
   * Clear all stored data
   * Note: This only clears localStorage, not cookies (too risky to clear all cookies)
   */
  clear(): void {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.clear();
      }
      console.warn('Storage cleared. Note: Cookies are not cleared for safety.');
    } catch (e) {
      console.error('Failed to clear storage:', e);
    }
  }

  /**
   * Check if storage is working properly
   */
  isAvailable(): boolean {
    return this.isLocalStorageAvailable() || this.canUseCookies();
  }

  /**
   * Test if cookies are available
   */
  private canUseCookies(): boolean {
    try {
      const testCookie = '__cookie_test__';
      this.setCookie(testCookie, 'test');
      const retrieved = this.getCookie(testCookie);
      this.removeCookie(testCookie);
      return retrieved === 'test';
    } catch (e) {
      return false;
    }
  }

  /**
   * Get debug info about storage availability
   */
  getDebugInfo(): object {
    return {
      localStorage: this.isLocalStorageAvailable(),
      cookies: this.canUseCookies(),
      userAgent: navigator.userAgent,
      isPrivateBrowsing: this.localStorageAvailable === false && typeof Storage !== 'undefined'
    };
  }
}

// Create singleton instance
export const storageService = new StorageService();

// Export for testing
export { StorageService };